import type { ReactNode } from 'react';

import { cx } from '@/utils/cx';

import type { PropertyTypeSchema } from '../../hooks/usePropertySchema';
import { EntityBadge } from '../badges/EntityBadge';
import { EntityBadgeById } from '../badges/EntityBadgeById';

/**
 * Association metadata for a formula's target entity type, keyed by
 * association_type_name. Distinguished from `PropertyTypeSchema` by the
 * absence of `field_type_name`. `properties` (when supplied) is the target
 * entity type's own property schema, used to resolve compound `assoc.prop`
 * tokens.
 */
export interface FormulaAssociationSchema {
  label: string;
  target_entity_type_name?: string;
  properties?: Record<string, PropertyTypeSchema>;
}

export type FormulaSchemaEntry = PropertyTypeSchema | FormulaAssociationSchema;

export interface FormulaDisplayProps {
  formula: unknown;
  schema?: Record<string, FormulaSchemaEntry>;
  /**
   * Organization ID, used to resolve uuid-shaped string literals into linked
   * entity badges. Optional — when omitted, uuid-shaped literals render as
   * plain highlighted text instead of firing a badge fetch against a bad org.
   */
  orgId?: number;
  className?: string;
  truncate?: boolean;
  /** Truncate the formula source to this many characters with "…". */
  maxLength?: number;
}

type TokenKind =
  | 'string'
  | 'number'
  | 'identifier'
  | 'keyword'
  | 'operator'
  | 'punctuation'
  | 'whitespace'
  | 'function'
  | 'plain';

interface FormulaToken {
  kind: TokenKind;
  value: string;
  start: number;
  /**
   * For an `identifier` token shaped exactly like `assoc.prop` (a single dot,
   * two non-empty segments), the two segments in order — so the association
   * can be resolved separately from the property it carries. Undefined for
   * single-segment identifiers and for deeper dotted paths (unsupported).
   */
  parts?: [string, string];
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function splitDottedIdentifier(value: string): [string, string] | undefined {
  const firstDot = value.indexOf('.');
  if (firstDot === -1) {
    return undefined;
  }
  if (value.indexOf('.', firstDot + 1) !== -1) {
    // More than one dot — compound depth beyond `assoc.prop` isn't resolved.
    return undefined;
  }
  return [value.slice(0, firstDot), value.slice(firstDot + 1)];
}

const FORMULA_KEYWORDS = new Set([
  'and',
  'or',
  'not',
  'in',
  'is',
  'true',
  'false',
  'null',
  'none',
]);

/**
 * Schema meta-entity types. Each formula token that resolves against the
 * schema renders as an entity badge keyed by the meta-type of the schema
 * object it references, so the leading icon tells the user what KIND of thing
 * the token is — a property or an association — and the label says which one.
 */
const META_PROPERTY_TYPE = 'ahoy_property_type';
const META_ASSOCIATION_TYPE = 'ahoy_association_type';

const TOKEN_CLASS: Record<TokenKind, string> = {
  string: 'text-utility-success-700 dark:text-utility-success-300',
  number: 'text-utility-blue-700 dark:text-utility-blue-300',
  identifier: 'text-fg-primary',
  keyword: 'text-fg-secondary font-medium',
  operator: 'text-fg-secondary',
  punctuation: 'text-fg-secondary',
  whitespace: '',
  function: 'text-utility-brand-700 dark:text-utility-brand-300',
  plain: 'text-fg-primary',
};

// Ordered by priority: strings, numbers, dotted identifiers, operators,
// punctuation, whitespace. No alternative overlaps another on its first
// character, so this stays linear — no catastrophic backtracking.
const FORMULA_TOKEN_REGEX =
  /'(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*"|\d+(?:\.\d+)?|[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*|==|!=|>=|<=|>|<|=|\+|-|\*|\/|[()[\],]|\s+/g;

function classifyMatch(value: string): TokenKind {
  const first = value[0];
  if (first === "'" || first === '"') {
    return 'string';
  }
  if (/^\d/.test(value)) {
    return 'number';
  }
  if (/^\s/.test(value)) {
    return 'whitespace';
  }
  if (/^[A-Za-z_]/.test(value)) {
    return FORMULA_KEYWORDS.has(value) ? 'keyword' : 'identifier';
  }
  if (/^[()[\],]$/.test(value)) {
    return 'punctuation';
  }
  return 'operator';
}

function tokenizeFormula(source: string): FormulaToken[] {
  const tokens: FormulaToken[] = [];
  FORMULA_TOKEN_REGEX.lastIndex = 0;

  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = FORMULA_TOKEN_REGEX.exec(source)) !== null) {
    for (let i = cursor; i < match.index; i++) {
      tokens.push({ kind: 'plain', value: source[i], start: i });
    }
    tokens.push({
      kind: classifyMatch(match[0]),
      value: match[0],
      start: match.index,
    });
    cursor = match.index + match[0].length;
  }
  for (let i = cursor; i < source.length; i++) {
    tokens.push({ kind: 'plain', value: source[i], start: i });
  }

  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i].kind !== 'identifier') {
      continue;
    }
    const parts = splitDottedIdentifier(tokens[i].value);
    if (parts) {
      tokens[i] = { ...tokens[i], parts };
      continue;
    }
    let next = i + 1;
    while (next < tokens.length && tokens[next].kind === 'whitespace') {
      next++;
    }
    if (next < tokens.length && tokens[next].value === '(') {
      tokens[i] = { ...tokens[i], kind: 'function' };
    }
  }

  return tokens;
}

/**
 * Truncate a token list to at most `maxLength` characters of source text while
 * preserving token boundaries, so schema/entity resolution never runs against a
 * partial token. A token that fits whole is kept intact (and stays resolvable);
 * the single token that straddles the limit is included only as an inert PLAIN
 * prefix — never a resolvable identifier — so a cut like `ahoy_amount_extra` →
 * `ahoy_amount` renders as plain text rather than the `ahoy_amount` chip.
 * Returns the retained tokens and whether any content was dropped.
 */
function truncateTokens(
  tokens: FormulaToken[],
  maxLength: number
): { tokens: FormulaToken[]; truncated: boolean } {
  const kept: FormulaToken[] = [];
  let used = 0;
  for (const token of tokens) {
    const remaining = maxLength - used;
    if (token.value.length <= remaining) {
      kept.push(token);
      used += token.value.length;
      continue;
    }
    // This token crosses the limit. Include only its prefix, stripped of the
    // kind/parts that would trigger resolution, so it renders as inert text.
    if (remaining > 0) {
      kept.push({
        kind: 'plain',
        value: token.value.slice(0, remaining),
        start: token.start,
      });
    }
    return { tokens: kept, truncated: true };
  }
  return { tokens: kept, truncated: false };
}

function coerceFormulaSource(formula: unknown): string {
  if (typeof formula === 'string') {
    return formula;
  }
  if (formula == null) {
    return '';
  }
  if (typeof formula === 'object') {
    // JSON.stringify is typed `string` but returns `undefined` at runtime for
    // values that serialize to nothing (e.g. an object whose toJSON() yields
    // undefined). Coalesce so the caller's `.trim()` never hits undefined.
    return JSON.stringify(formula) ?? '';
  }
  return String(formula);
}

function isAssociationSchemaEntry(
  entry: FormulaSchemaEntry
): entry is FormulaAssociationSchema {
  return !('field_type_name' in entry);
}

function renderPropertyToken(
  token: FormulaToken,
  schema: Record<string, FormulaSchemaEntry>
): ReactNode {
  // Exact-token lookup only: a compound path like `foo.bar` must not resolve
  // to the `foo` chip, which would hide `.bar` from the read-only formula.
  const entry = schema[token.value];
  if (!entry) {
    return undefined;
  }
  // Render as an entity badge keyed by the schema object's meta-type: a bare
  // association (no `.prop` suffix, e.g. `ahoy_deal_stage != null`) gets the
  // association-type icon, a property gets the property-type icon. The badge
  // is static (never linked) — schema references are not entity instances.
  const isAssoc = isAssociationSchemaEntry(entry);
  return (
    <EntityBadge
      key={token.start}
      variant='inline'
      interactive={false}
      className='font-body'
      state={{
        kind: 'data',
        data: {
          id: token.value,
          entityType: isAssoc ? META_ASSOCIATION_TYPE : META_PROPERTY_TYPE,
          displayName: entry.label,
        },
      }}
    />
  );
}

function renderAssociationToken(
  token: FormulaToken,
  parts: [string, string],
  schema: Record<string, FormulaSchemaEntry>
): ReactNode {
  const [assocName, propName] = parts;
  const assocEntry = schema[assocName];
  if (!assocEntry || !isAssociationSchemaEntry(assocEntry)) {
    return undefined;
  }
  const propEntry = assocEntry.properties?.[propName];
  if (!propEntry) {
    return undefined;
  }
  return (
    <span
      key={token.start}
      className='inline-flex items-center gap-1 align-middle'
    >
      <EntityBadge
        variant='inline'
        interactive={false}
        className='font-body'
        state={{
          kind: 'data',
          data: {
            id: assocName,
            entityType: META_ASSOCIATION_TYPE,
            displayName: assocEntry.label,
          },
        }}
      />
      <span className={TOKEN_CLASS.operator}>.</span>
      <EntityBadge
        variant='inline'
        interactive={false}
        className='font-body'
        state={{
          kind: 'data',
          data: {
            id: `${assocName}.${propName}`,
            entityType: META_PROPERTY_TYPE,
            displayName: propEntry.label,
          },
        }}
      />
    </span>
  );
}

function renderEntityLiteralToken(
  token: FormulaToken,
  orgId: number | undefined
): ReactNode {
  if (!orgId) {
    return undefined;
  }
  const raw = token.value.slice(1, -1);
  if (!UUID_PATTERN.test(raw)) {
    return undefined;
  }
  return (
    <span key={token.start}>
      <EntityBadgeById
        entityId={raw}
        orgId={orgId}
        variant='inline'
        className='font-body'
      />
    </span>
  );
}

function renderFormulaToken(
  token: FormulaToken,
  schema: Record<string, FormulaSchemaEntry> | undefined,
  orgId: number | undefined
): ReactNode {
  if (token.kind === 'identifier' && schema) {
    const rendered = token.parts
      ? renderAssociationToken(token, token.parts, schema)
      : renderPropertyToken(token, schema);
    if (rendered) {
      return rendered;
    }
  }

  if (token.kind === 'string') {
    const rendered = renderEntityLiteralToken(token, orgId);
    if (rendered) {
      return rendered;
    }
  }

  return (
    <span key={token.start} className={TOKEN_CLASS[token.kind]}>
      {token.value}
    </span>
  );
}

export function FormulaDisplay({
  formula,
  schema,
  orgId,
  className,
  truncate,
  maxLength,
}: FormulaDisplayProps) {
  const src = coerceFormulaSource(formula);

  if (src.trim() === '') {
    return <span className={cx('text-fg-secondary', className)}>—</span>;
  }

  // Tokenize the full source first, then apply the maxLength cap at token
  // boundaries. Truncating the raw string before tokenizing would let a cut
  // land mid-token (`ahoy_amount_extra` → `ahoy_amount`), and the prefix would
  // then resolve to a different property's chip. Honoring the same character
  // cap as other label branches (e.g. CrmPropertyField's 150-char limit); the
  // full source stays available on hover via the title attribute.
  const allTokens = tokenizeFormula(src);
  const { tokens, truncated: isTruncated } =
    maxLength !== undefined
      ? truncateTokens(allTokens, maxLength)
      : { tokens: allTokens, truncated: false };

  return (
    <code
      className={cx(
        truncate
          ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
          : 'whitespace-pre-wrap break-words',
        className
      )}
      title={truncate || isTruncated ? src : undefined}
    >
      {tokens.map(token => renderFormulaToken(token, schema, orgId))}
      {isTruncated && <span className={TOKEN_CLASS.plain}>…</span>}
    </code>
  );
}
